import React, { useMemo, useState } from "react";
import { RankingTable, PlayerCard, Player, RatingRow, Badge } from "../../components/handball";
import Avatar from "react-avatar";

type Props = {
  rows: RatingRow[];
  topPlayer: Player;
};

const PAGE_SIZE = 10;

export default function Rankings({ rows, topPlayer }: Props): JSX.Element {
  const top10 = useMemo(() => rows.slice(0, 10), [rows]);
  const rest = useMemo(() => rows.slice(10), [rows]);

  const [page, setPage] = useState<number>(1);
  const pageCount = Math.max(1, Math.ceil(rest.length / PAGE_SIZE));
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rest.slice(start, start + PAGE_SIZE);
  }, [rest, page]);

  const CardStat: React.FC<{ label: string; value: string; }> = ({ label, value }) => (
    <div style={{ minWidth: 60 }}>
      <div className="card__meta" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontWeight: 800 }}>{value}</div>
    </div>
  );

  return (
    <div className="u-col u-gap-12">
      {/* Top 10 section */}
      <div className="u-row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <div className="card__header">Top 10</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(300px, 1fr))", gap: 12 }}>
        {top10.map((r, idx) => {
          const games = r.wins + r.losses;
          const winRate = games === 0 ? "—" : `${Math.round((r.wins / games) * 100)}%`;
          const rank = idx + 1;
          const accent = rank === 1 ? "#FFD700" : rank === 2 ? "#C0C0C0" : rank === 3 ? "#CD7F32" : "var(--border-subtle)";
          const bg = rank === 1
            ? "linear-gradient(90deg, rgba(255,215,0,0.08), transparent)"
            : rank === 2
              ? "linear-gradient(90deg, rgba(192,192,192,0.06), transparent)"
              : rank === 3
                ? "linear-gradient(90deg, rgba(205,127,50,0.06), transparent)"
                : undefined;
          const avatarSize = rank === 1 ? 56 : rank === 2 ? 52 : rank === 3 ? 48 : 44;
          return (
            <div
              key={r.player.id}
              className="card"
              style={{
                padding: 12,
                background: bg,
                border: `1px solid ${accent}`,
                boxShadow: rank <= 3 ? `0 2px 10px rgba(0,0,0,0.06)` : undefined,
              }}
            >
              <div className="u-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div className="u-row" style={{ gap: 12, alignItems: "center" }}>
                  <div style={{
                    width: avatarSize,
                    height: avatarSize,
                    borderRadius: "50%",
                    border: `3px solid ${accent}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}>
                    <Avatar name={r.player.name} size={`${avatarSize}`} round={true} />
                  </div>
                  <div className="card__header">{r.player.name}</div>
                </div>
                <div className="u-row" style={{ gap: 12, alignItems: "center" }}>
                  <Badge kind="info">Rating {Math.round(r.rating)}</Badge>
                  <div
                    aria-label={`Rank ${rank}`}
                    title={`Rank ${rank}`}
                    style={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      border: `2px solid ${accent}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 900,
                      color: "var(--text-primary)",
                      background: "rgba(0,0,0,0.02)",
                    }}
                  >
                    {rank}
                  </div>
                </div>
              </div>
              <div className="u-row" style={{ gap: 16, marginTop: 8 }}>
                <CardStat label="Wins" value={`${r.wins}`} />
                <CardStat label="Losses" value={`${r.losses}`} />
                <CardStat label="Win %" value={winRate} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Removed spotlight card per request */}

      {/* Remaining players with pagination */}
      <div className="u-row" style={{ marginTop: 8, justifyContent: "space-between", alignItems: "baseline" }}>
        <div className="card__header">All Players</div>
        <div className="card__meta">Page {page} of {pageCount}</div>
      </div>

      <RankingTable rows={pageRows} />

      <div className="u-row" style={{ gap: 8, justifyContent: "space-between" }}>
        <div className="u-row" style={{ gap: 8 }}>
          <button className="btn btn--ghost" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
          <button className="btn btn--ghost" disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}>Next</button>
        </div>
        <div className="card__meta">Showing {pageRows.length} of {rest.length}</div>
      </div>
    </div>
  );
}
